import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {deleteScene, getScene, saveScene, searchScenes, SceneFilter} from "@/scenes/SceneApi.ts";

export const sceneKeys = {
    all: ["scenes"] as const,
    lists: () => [...sceneKeys.all, "list"] as const,
    list: (filter: SceneFilter, pageSize: number) => [...sceneKeys.lists(), filter, pageSize] as const,
    detail: (id: string) => [...sceneKeys.all, "detail", id] as const,
};

export function useSceneQuery(id?: string) {
    return useQuery({
        queryKey: sceneKeys.detail(id ?? ""),
        queryFn: ({signal}) => getScene(id!, signal),
        enabled: Boolean(id),
    });
}

export function useScenesQuery(filter: SceneFilter, pageSize = 50) {
    const query = useInfiniteQuery({
        queryKey: sceneKeys.list(filter, pageSize),
        initialPageParam: 0,
        queryFn: ({pageParam, signal}) => searchScenes({page: pageParam, size: pageSize}, filter, signal),
        getNextPageParam: lastPage => {
            const nextPage = lastPage.page.number + 1;
            return nextPage < lastPage.page.totalPages ? nextPage : undefined;
        },
    });
    const scenes = query.data?.pages.flatMap(page => page.content) ?? [];

    return {
        ...query,
        scenes,
        totalCount: query.data?.pages[0]?.page.totalElements ?? 0,
        rowCount: scenes.length + (query.hasNextPage ? 1 : 0),
        loadMore: async () => {
            if (query.hasNextPage) await query.fetchNextPage({cancelRefetch: false});
        },
    };
}

export function useDeleteSceneMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteScene,
        onSuccess: (_, id) => {
            queryClient.removeQueries({queryKey: sceneKeys.detail(id)});
            void queryClient.invalidateQueries({queryKey: sceneKeys.lists()});
        },
    });
}

export function useSaveSceneMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: saveScene,
        onSuccess: scene => {
            queryClient.setQueryData(sceneKeys.detail(scene.id), scene);
            void queryClient.invalidateQueries({queryKey: sceneKeys.lists()});
        },
    });
}
